import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import { t } from 'src/translations'

import isLatLong from 'validator/lib/isLatLong'

import {
  NearbyRestaurantSearchInput,
  RxSearchQueryVariables,
  useRestaurantByShortUrlQuery,
  useSimilarRxSearchQuery
} from 'src/apollo/onlineOrdering'
import {
  useToastLocalConfigQuery,
  useToastLocalSiteDataQuery
} from 'src/apollo/sites'
import { DebugContextProvider } from 'src/shared/components/common/debug/DebugContext'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import RestaurantContextProvider from 'shared/components/common/restaurant_context/RestaurantContext'
import { RestaurantRoutesContextProvider } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { RxDataUsage } from 'shared/js/hooks/useDefaultSiteData'

import Meta from 'public/components/default_template/meta/Meta'
import { SiftMeta } from 'public/components/default_template/meta/sift/SiftMeta'
import {
  RestaurantProfileRestaurantType,
  SimilarRxSearchResultRestaurantType
} from 'public/components/default_template/restaurant_profile/profileUtils'
import { StructuredDataProvider } from 'public/components/seo/context'
import {
  TOAST_APP_ORDER_PATH,
  TOAST_LOCAL_ORDER_PATH,
  TOAST_LOCAL_PATH
} from 'public/js/siteUtilities'

import { resources } from 'config'
import NotFound404 from 'shared/components/errors/NotFound404'
import { OptionalToastLocalSiteData } from 'src/shared/js/types'

export type WrappedProfilePageProps = {
  restaurantProfileData: RestaurantProfileRestaurantType
  nearbyRxs?: SimilarRxSearchResultRestaurantType[]
  optionalSiteData?: OptionalToastLocalSiteData
}

type Props = {
  respectToastLocalConfig: boolean
  baseHomePath?: string
  baseOrderPath?: string
  host?: string
  useSlugFirstPattern?: boolean
}

export const RestaurantProfilePageWrapper = ({
  children,
  respectToastLocalConfig = true,
  baseHomePath = TOAST_LOCAL_PATH,
  baseOrderPath = TOAST_LOCAL_ORDER_PATH,
  host = resources.toastLocalHost,
  useSlugFirstPattern = false
}: { children: (p: WrappedProfilePageProps) => React.ReactNode } & Props) => {
  const { slug }: { slug: string } = useParams()

  const ooClient = useOOClient()
  const { data, loading } = useRestaurantByShortUrlQuery({
    variables: {
      shortUrl: decodeURIComponent(slug || '')
    },
    client: ooClient
  })

  const { data: tlData, loading: tlLoading } = useToastLocalConfigQuery({
    variables: {
      restaurantGuid:
        data?.restaurantV2ByShortUrl.__typename === 'Restaurant'
          ? data.restaurantV2ByShortUrl.guid
          : ''
    },
    skip:
      data?.restaurantV2ByShortUrl.__typename !== 'Restaurant' ||
      !data.restaurantV2ByShortUrl.guid
  })

  const variables: RxSearchQueryVariables | null = useMemo(() => {
    const rx =
      data?.restaurantV2ByShortUrl.__typename === 'Restaurant'
        ? data.restaurantV2ByShortUrl
        : null
    if (!rx?.location.latitude || !rx?.location.longitude) {
      return null
    }
    const vars: NearbyRestaurantSearchInput = {
      limit: 7,
      coordinates: {
        latitude: rx.location.latitude,
        longitude: rx.location.longitude
      },
      searchQuery: rx.cuisineType || ''
    }
    return { input: vars }
  }, [data])

  const validLatLong = useMemo(
    () =>
      data?.restaurantV2ByShortUrl?.__typename === 'Restaurant' &&
      data.restaurantV2ByShortUrl.location.latitude &&
      data.restaurantV2ByShortUrl.location.longitude &&
      isLatLong(
        `(${data.restaurantV2ByShortUrl.location.latitude},${data.restaurantV2ByShortUrl.location.longitude})`
      ),
    [data?.restaurantV2ByShortUrl]
  )

  // useRxSearchQuery pulls Rx availability and other data that can take a lot of time to fetch.
  // Since we don't need that here, use a pared down query.
  const { data: searchData, loading: searchLoading } = useSimilarRxSearchQuery({
    // Skip ensures presence of variables
    variables: variables!,
    skip: !validLatLong || !variables,
    client: ooClient
  })

  const nearbyRxs = useMemo(() => {
    return searchData?.nearbyRestaurantSearch.results
      .filter((rx) => rx.restaurant.shortUrl !== slug)
      .map((r) => r.restaurant)
  }, [slug, searchData?.nearbyRestaurantSearch.results])

  // if this rx also has a site, query any data to be used in Toast Local
  // if use-cases expand, this could be stored in a separate, Toast Local only context.
  const { data: optionalSiteData } = useToastLocalSiteDataQuery({
    variables: { shortUrl: decodeURIComponent(slug || '') },
    skip: !slug
  })

  if (tlLoading || loading || searchLoading) {
    return null
  }

  const toastLocalDisabled =
    respectToastLocalConfig && !tlData?.toastLocalConfig?.enabled

  if (
    toastLocalDisabled ||
    !data?.restaurantV2ByShortUrl ||
    data.restaurantV2ByShortUrl.__typename !== 'Restaurant'
  ) {
    return <NotFound404 ctaUrl={baseHomePath} />
  }

  const homePath = baseHomePath
  // toast.app and book.toasttab.com use /r/:slug/order pattern (slug first)
  // All other paths use /:slug pattern (e.g., /local/order/:slug)
  const profilePath = useSlugFirstPattern
    ? `${TOAST_APP_ORDER_PATH}/${slug}${baseOrderPath}`
    : `${baseOrderPath}/${slug}`
  const accountPath = useSlugFirstPattern
    ? `${TOAST_APP_ORDER_PATH}/${slug}${baseOrderPath}/account`
    : `${baseOrderPath}/${slug}/account`
  const profilePathPattern = useSlugFirstPattern
    ? `${TOAST_APP_ORDER_PATH}/:slug${baseOrderPath}`
    : `${baseOrderPath}/:slug`

  return (
    <>
      <SiftMeta />
      <Meta
        title={t('restaurant-profile-page-wrapper.page-title', {
          restaurantName: data.restaurantV2ByShortUrl.name
        })}
        primaryColor='#FF4C00'
        icon='https://cdn.toasttab.com/static/42fd6cabe2aa1dc8ad50e0d66f30ce9e3de4aa36/images/favicon.ico'
        domain={host}
      />
      <DebugContextProvider>
        {/* Beware: seting sitesUsage to Optional or Required will lead to feature spill over of OnlineOrdering/Websites into Toast Local */}
        <RestaurantContextProvider
          ooUsage={RxDataUsage.Required}
          sitesUsage={RxDataUsage.None}
          isEditor={false}
        >
          <RestaurantRoutesContextProvider
            shortUrl={slug}
            homePath={homePath}
            orderPath={profilePath}
            orderPathPattern={profilePathPattern}
            checkoutPathPrefix={profilePath}
            confirmPathPrefix={profilePath}
            accountPath={accountPath}
          >
            <StructuredDataProvider>
              {children({
                restaurantProfileData:
                  data.restaurantV2ByShortUrl as RestaurantProfileRestaurantType,
                nearbyRxs,
                optionalSiteData: optionalSiteData?.restaurantByShortUrl
              })}
            </StructuredDataProvider>
          </RestaurantRoutesContextProvider>
        </RestaurantContextProvider>
      </DebugContextProvider>
    </>
  )
}

export default RestaurantProfilePageWrapper
